import { useCallback, useMemo } from 'react';
import { useHasPermission } from '@/entities/profile';
import {
  useCreateSystemPreset,
  useDeleteSystemPreset,
  useSystemPresets,
  useUpdateSystemPreset,
} from '@/entities/system-presets';
import { stableStringify } from '@/shared/lib';
import { pickPresetSnapshot } from './store';
import { buildSystemPresetId, getSectionTableName, isPresetKeyOfTable, parseSystemPresetId } from './tableKeys';
import { useSettings } from './useSettings';
import {
  TABLE_PRESET_SNAPSHOT_KEYS,
  TableSettings,
  type TablePresetSnapshotKey,
  type TableViewSnapshot,
} from './types';

/** Описание пресета для UI: id-ключ пресета, имя, снимок настроек и права на него. */
export type TablePreset = {
  /** Полный ключ пресета: личного — в `tables` (`<tableName>:<uuid>`), системного — `<tableName>:system:<id>` */
  id: string;
  /** Пользовательское имя пресета */
  name: string;
  /** Источник внешней выборки, из которой создан пресет; `undefined` — пресет заведён вручную */
  selectionSource?: string;
  /** Пресет закреплён отдельным пунктом левого меню */
  pinned: boolean;
  /** Снимок настроек таблицы (только поля из TABLE_PRESET_SNAPSHOT_KEYS) */
  settings: TableSettings;
  /** Пресет системный — общий для всех пользователей */
  isSystem: boolean;
  /** Текущий пользователь вправе править этот пресет: свой личный либо системный при разрешении */
  isEditable: boolean;
};

/**
 * Стабильное сериализующее сравнение двух снимков пресета.
 * Используется для определения «грязного» состояния (текущее ≠ активный пресет).
 * Сравниваются только поля из {@link TABLE_PRESET_SNAPSHOT_KEYS}.
 */
const stringifySnapshot = (settings: TableSettings | undefined): string => {
  if (!settings) return '{}';
  const parts: string[] = [];
  for (const key of TABLE_PRESET_SNAPSHOT_KEYS) {
    parts.push(`${key}=${stableStringify(settings[key])}`);
  }
  return parts.join('|');
};

const byName = (a: TablePreset, b: TablePreset): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Хук работы с именованными пресетами настроек таблицы.
 *
 * Личные пресеты хранятся в `settings.tables` под ключами `<tableName>:<uuid>` и отличаются
 * от рабочих записей наличием поля `name`. Системные живут на сервере и попадают в тот же
 * список; личное у них только применение, а название, вид и закрепление общие для всех.
 * Список пресетов один на таблицу, а активный пресет и вид — свои у каждого раздела: они
 * читаются из рабочей записи `tables[sectionKey]`, и туда же пишут все экшены вида
 * (применение, вход в слой, снятие).
 *
 * Общий пресет меняется без ведома применившего, поэтому расхождение текущего вида с пресетом
 * разделено на два: донастройку пользователя (`isDirty`) и устаревание применённого вида
 * (`isOutdated`) — их различает отметка вида на момент применения (`activePresetView`).
 * Подхват нового вида пресета — {@link useActivePresetRefresh}.
 *
 * @param sectionKey - Ключ рабочей записи: имя таблицы (например, `'dialogsListV2'`)
 * либо ключ раздела закреплённого пресета.
 */
export const useTablePresets = (sectionKey: string) => {
  const tableName = getSectionTableName(sectionKey);
  const {
    userId,
    tables,
    setTableSettings,
    saveTablePreset,
    updateTablePreset,
    applyTablePreset,
    enterTableView,
    deleteTablePreset,
    renameTablePreset,
    setTablePresetPinned,
    clearActiveTablePreset,
    exitTableSelection,
  } = useSettings();

  const canManageSystemPresets = useHasPermission('ADMIN_SYSTEM_PRESET');
  const { data: systemPresetsData, isLoading: isSystemPresetsLoading } = useSystemPresets(tableName);
  const { mutate: createSystemPreset } = useCreateSystemPreset();
  const { mutate: updateSystemPreset } = useUpdateSystemPreset();
  const { mutate: deleteSystemPreset } = useDeleteSystemPreset();

  const systemPresets = useMemo<TablePreset[]>(() => {
    const list = (systemPresetsData?.items ?? []).map<TablePreset>((preset) => ({
      id: buildSystemPresetId(tableName, preset.id),
      name: preset.name,
      pinned: preset.isPinned,
      settings: preset.settings,
      isSystem: true,
      isEditable: canManageSystemPresets,
    }));
    list.sort(byName);
    return list;
  }, [systemPresetsData, tableName, canManageSystemPresets]);

  const personalPresets = useMemo<TablePreset[]>(() => {
    const list: TablePreset[] = [];
    for (const [key, value] of Object.entries(tables)) {
      if (!isPresetKeyOfTable(key, tableName)) continue;
      if (!value.name) continue;
      list.push({
        id: key,
        name: value.name,
        selectionSource: value.selectionSource,
        pinned: value.pinnedToMenu === true,
        settings: value,
        isSystem: false,
        isEditable: true,
      });
    }
    list.sort(byName);
    return list;
  }, [tables, tableName]);

  // Системные идут первыми: это общий для всех набор, личные пользователь заводит поверх него.
  const presets = useMemo<TablePreset[]>(
    () => [...systemPresets, ...personalPresets],
    [systemPresets, personalPresets],
  );

  // Набор пресетов известен целиком: личные лежат в настройках пользователя и известны сразу,
  // системные — общие и приходят с сервера отдельно. Пока их не получили, отсутствие пресета
  // в наборе ещё ничего не означает, и выводы «такого пресета нет» на нём строить нельзя.
  // Раздел без пресетов запроса не делает — там набор известен сразу и он пуст.
  const isPresetListKnown = !isSystemPresetsLoading;

  const systemPresetById = useMemo(() => new Map(systemPresets.map((preset) => [preset.id, preset])), [systemPresets]);

  const activeRecord = tables[sectionKey];
  const activePresetId = activeRecord?.activePresetId;
  const previousPresetId = activeRecord?.previousPresetId;
  const activePresetView = activeRecord?.activePresetView;
  const activePreset = useMemo(() => presets.find((p) => p.id === activePresetId) ?? null, [presets, activePresetId]);

  // Пресет изменился после того, как был применён, а пользователь его вида не касался: применённый
  // вид устарел. Без отметки о применении (набор от прежней версии) устаревание неотличимо
  // от донастройки — считаем видом пользователя и молча ничего не подменяем.
  const isOutdated = useMemo(() => {
    if (!activePreset || !activePresetView) return false;
    const appliedView = stringifySnapshot(activePresetView);
    return stringifySnapshot(activeRecord) === appliedView && stringifySnapshot(activePreset.settings) !== appliedView;
  }, [activePreset, activePresetView, activeRecord]);

  // Донастройка — расхождение с видом пресета, сделанное самим пользователем. Расхождение,
  // пришедшее с правкой общего пресета, донастройкой не считается: предлагать пользователю
  // сохранить копию или записать в пресет чужой вид, которого он не выбирал, нельзя.
  const isDirty = useMemo(() => {
    if (!activePreset || isOutdated) return false;
    return stringifySnapshot(activeRecord) !== stringifySnapshot(activePreset.settings);
  }, [activePreset, activeRecord, isOutdated]);

  const savePresetAs = useCallback(
    (name: string, selectionSource?: string): string => saveTablePreset(userId, sectionKey, name, selectionSource),
    [saveTablePreset, userId, sectionKey],
  );

  // Отметка о виде, с которым пресет считается применённым: от неё отсчитывается донастройка
  // пользователя, поэтому обновляется везде, где вид таблицы сходится с видом пресета.
  const rememberAppliedView = useCallback(
    (view: TableSettings | undefined) => setTableSettings(sectionKey, { activePresetView: pickPresetSnapshot(view) }),
    [setTableSettings, sectionKey],
  );

  const saveCurrentToActivePreset = useCallback(() => {
    if (!activePresetId) return;
    const serverId = parseSystemPresetId(activePresetId, tableName);
    if (serverId === null) {
      updateTablePreset(userId, sectionKey, activePresetId);
      return;
    }
    const snapshot = pickPresetSnapshot(activeRecord);
    // Записанный вид становится точкой отсчёта только после ответа сервера: отказ по правам
    // оставляет донастройку пользователя при нём — вместе с предложением сохранить её копией.
    updateSystemPreset({ id: serverId, settings: snapshot }, { onSuccess: () => rememberAppliedView(snapshot) });
  }, [
    updateTablePreset,
    updateSystemPreset,
    rememberAppliedView,
    userId,
    sectionKey,
    tableName,
    activePresetId,
    activeRecord,
  ]);

  const apply = useCallback(
    (presetId: string) => applyTablePreset(userId, sectionKey, presetId, systemPresetById.get(presetId)?.settings),
    [applyTablePreset, userId, sectionKey, systemPresetById],
  );

  const enterView = useCallback(
    (view: TableViewSnapshot, options?: { backup?: boolean }) => enterTableView(userId, sectionKey, view, options),
    [enterTableView, userId, sectionKey],
  );

  const remove = useCallback(
    (presetId: string) => {
      const serverId = parseSystemPresetId(presetId, tableName);
      if (serverId === null) {
        deleteTablePreset(userId, presetId);
        return;
      }
      // Удалённый системный пресет исчезает у всех, поэтому его применение снимается уже
      // после ответа сервера: отказ по правам оставляет пресет применённым.
      deleteSystemPreset(serverId, {
        onSuccess: () => {
          if (activePresetId === presetId) clearActiveTablePreset(userId, sectionKey);
        },
      });
    },
    [deleteTablePreset, deleteSystemPreset, clearActiveTablePreset, userId, sectionKey, tableName, activePresetId],
  );

  const rename = useCallback(
    (presetId: string, name: string) => {
      const serverId = parseSystemPresetId(presetId, tableName);
      if (serverId === null) {
        renameTablePreset(userId, presetId, name);
        return;
      }
      updateSystemPreset({ id: serverId, name });
    },
    [renameTablePreset, updateSystemPreset, userId, tableName],
  );

  const setPinned = useCallback(
    (presetId: string, pinned: boolean) => {
      const serverId = parseSystemPresetId(presetId, tableName);
      if (serverId === null) {
        setTablePresetPinned(userId, presetId, pinned);
        return;
      }
      updateSystemPreset({ id: serverId, isPinned: pinned });
    },
    [setTablePresetPinned, updateSystemPreset, userId, tableName],
  );

  const makeSystem = useCallback(
    (presetId: string) => {
      const preset = personalPresets.find((item) => item.id === presetId);
      if (!preset) return;
      // Закрепление не переносится: у личного пресета оно личное, у системного — общее для всех.
      createSystemPreset(
        { tableName, name: preset.name, settings: pickPresetSnapshot(preset.settings) },
        {
          onSuccess: (created) => {
            const wasActive = activePresetId === presetId;
            deleteTablePreset(userId, presetId);
            if (wasActive) {
              applyTablePreset(userId, sectionKey, buildSystemPresetId(tableName, created.id), created.settings);
            }
          },
        },
      );
    },
    [
      createSystemPreset,
      deleteTablePreset,
      applyTablePreset,
      personalPresets,
      userId,
      sectionKey,
      tableName,
      activePresetId,
    ],
  );

  const clearActive = useCallback(
    () => clearActiveTablePreset(userId, sectionKey),
    [clearActiveTablePreset, userId, sectionKey],
  );

  // Снимок системного пресета, поверх которого открыта выборка: в настройках пользователя его нет,
  // и без снимка снятие выборки вернуло бы личный вид вместо вида пресета.
  const previousSystemPresetSnapshot = useMemo(
    () => (previousPresetId ? systemPresetById.get(previousPresetId)?.settings : undefined),
    [previousPresetId, systemPresetById],
  );

  const exitSelection = useCallback(
    (selectionFilterId: string) =>
      exitTableSelection(userId, sectionKey, selectionFilterId, previousSystemPresetSnapshot),
    [exitTableSelection, userId, sectionKey, previousSystemPresetSnapshot],
  );

  return {
    presets,
    isPresetListKnown,
    activePresetId,
    activePreset,
    activePresetView,
    isDirty,
    isOutdated,
    canManageSystemPresets,
    rememberAppliedView,
    savePresetAs,
    saveCurrentToActivePreset,
    apply,
    enterView,
    remove,
    rename,
    setPinned,
    makeSystem,
    clearActive,
    exitSelection,
  };
};

// Чтобы тип TablePresetSnapshotKey не выпал при анализе использований
export type { TablePresetSnapshotKey };
